/**
 * How it works — onboarding / reference page for admin users.
 * Shows the end-to-end job lifecycle, role responsibilities, and FAQs so a
 * new dispatcher can self-serve. Static content, no API calls.
 */
import { useState, type ReactNode } from 'react';

import { BRAND } from '@fin/shared/brand';

import { Card, PageHeader } from '../components/ui';
import { PageIcon, PageAccent } from '../components/PageIcons';

interface Step {
  num: number;
  title: string;
  body: string;
  tip?: string;
}

const STEPS: Step[] = [
  {
    num: 1,
    title: 'Create a customer',
    body: 'Go to Customers → + Customer. Enter name, phone, WhatsApp number (if different) and address. The customer record is reused on every job.',
    tip: 'Address area + emirate are required so jobs can be filtered by location.',
  },
  {
    num: 2,
    title: 'Add your workers',
    body: 'Workers → + Worker. We provision a Cognito login for them automatically and Cognito emails them a temporary password. They sign in at app.fixitninjaz.com on their phone, set a new password, and they’re ready to receive jobs.',
    tip: 'Workers must have active = true. Inactive workers don’t appear in the assignment dropdown.',
  },
  {
    num: 3,
    title: 'Create a job',
    body: 'Jobs → + Create job. Pick the customer, service type, schedule, and (optionally) the worker. If you assign a worker now the job opens in the Assigned tab. If you don’t, it sits in Scheduled until you assign one from the row dropdown.',
    tip: 'Set priority = urgent to surface the job on the dashboard counter and at the top of the worker’s Today list.',
  },
  {
    num: 4,
    title: 'Worker is notified instantly',
    body: 'The moment a worker is assigned, three things happen in parallel: a web push notification is sent to their phone, a row appears in their notification bell, and the reminder Lambda starts watching the SLA clock.',
    tip: 'Push works on Android out of the box. iOS requires the worker to first install the worker app to their Home Screen (Apple restriction).',
  },
  {
    num: 5,
    title: 'Worker drives the job from the field',
    body: 'In the worker app the worker opens the job and taps in order: On the way → Start → upload proof photos (before / after / parts) → Complete. Each tap updates the status in real-time and writes a log entry visible to admin.',
    tip: 'Workers can also tap Hold (e.g. waiting on parts). Admin sees the job in the on_hold tab and can re-dispatch later.',
  },
  {
    num: 6,
    title: 'Admin tracks live status',
    body: 'Dashboard shows live counts. Jobs page shows every job with its current status. Today shows everything happening in the next 24h. Status pills colour-code the lifecycle so you can spot jams at a glance.',
    tip: 'Use the Status dropdown on a Jobs row only as an override — e.g. if a worker can’t reach the app. Workers should normally drive transitions themselves.',
  },
  {
    num: 7,
    title: 'Send a customer update via WhatsApp',
    body: 'Use the WhatsApp dropdown on a Jobs row to open WhatsApp with a pre-filled, situation-aware message (Job assigned, On the way, Job started, Completed + feedback). Nothing is sent automatically — you press Send in WhatsApp.',
    tip: 'The customer’s WhatsApp number is used if set, otherwise their phone number.',
  },
  {
    num: 8,
    title: 'Collect feedback after completion',
    body: 'When a job is marked Completed, send the Completed + feedback WhatsApp template. The customer gets a link to feedback.fixitninjaz.com/r/{jobId}. Their reply lands in your Feedback inbox.',
    tip: 'Click a Customer name in Feedback to jump straight to that customer’s job detail.',
  },
];

interface FaqItem {
  q: string;
  a: string;
}

const FAQS: FaqItem[] = [
  {
    q: 'How do I add another admin?',
    a: 'Settings → Admin users (visible to super_admin only). Enter the email and pick a role; Cognito will email them a temporary password. They’re forced to set a new password and enrol TOTP on first sign-in.',
  },
  {
    q: 'A worker forgot their password — what do they do?',
    a: 'On the worker login screen they tap Forgot password? Cognito emails them a 6-digit code; they enter it with a new password and sign in. The same flow exists on the admin login screen.',
  },
  {
    q: 'A job is stuck in the wrong status. Can I fix it?',
    a: 'Yes — use the Status dropdown on the Jobs row to set the correct status. This writes a log entry attributed to your admin account.',
  },
  {
    q: 'Can a worker see other workers’ jobs?',
    a: 'No. The API enforces that a worker can only see jobs where assignedWorkerId matches their own user id. Admins and dispatchers see everything.',
  },
  {
    q: 'What happens if a worker doesn’t accept a job?',
    a: 'The reminder Lambda runs every 5 minutes and re-pings them. If the SLA elapses you’ll see an alert in the dashboard so you can re-assign.',
  },
  {
    q: 'Where do I see proof photos?',
    a: 'Open the job (Jobs → click customer name) — the Proof section shows every before / after / parts photo the worker uploaded with timestamps.',
  },
  {
    q: 'How do I export a list of jobs?',
    a: 'On the Jobs page, click ? Export CSV. The export respects the current tab and filter so you can export just one status, one worker, etc.',
  },
  {
    q: 'How does push notification work on iPhone?',
    a: 'Apple only allows web push when the app is installed to the Home Screen. Tell your worker to open app.fixitninjaz.com in Safari, tap Share ? Add to Home Screen, then open it from the home icon and enable push.',
  },
];

const ROLES: { name: string; tag: string; can: string[] }[] = [
  {
    name: 'super_admin',
    tag: 'Owner',
    can: [
      'Everything an admin can do',
      'Invite / revoke other admins (Settings)',
      'Manage feature flags (when enabled)',
    ],
  },
  {
    name: 'admin',
    tag: 'Manager',
    can: [
      'Create / edit customers, workers, jobs',
      'Assign workers, override job status',
      'Send WhatsApp messages, view feedback',
      'See all proof photos and job logs',
    ],
  },
  {
    name: 'dispatcher',
    tag: 'Coordinator',
    can: [
      'Read-only across most pages',
      'Assign workers and update job status',
      'Cannot create / delete users',
    ],
  },
  {
    name: 'marketing',
    tag: 'Content',
    can: [
      'Write, publish and unpublish blog posts (Content)',
      'Use the AI writing helpers (daily cap applies)',
      'No access to customers, jobs, workers or invoices',
    ],
  },
  {
    name: 'worker',
    tag: 'Field',
    can: [
      'Sees only their own assigned jobs',
      'Uses the worker app (app.fixitninjaz.com)',
      'Updates status, uploads proof photos',
    ],
  },
];

export default function HowItWorksPage() {
  return (
    <>
      <PageHeader
        title="How it works"
        subtitle="Onboarding guide · lifecycle · FAQs"
        icon={PageIcon.settings}
        accent={{ from: '#0F766E', to: '#14B8A6' }}
      />

      <Card className="mb-4 overflow-hidden">
        <div className="border-b border-stone-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-stone-800">End-to-end job lifecycle</h2>
          <p className="mt-0.5 text-xs text-stone-500">
            From customer creation to feedback — the eight steps every job goes through.
          </p>
        </div>
        <ol className="divide-y divide-stone-100">
          {STEPS.map((s) => (
            <li key={s.num} className="flex gap-4 px-4 py-4 sm:px-6">
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white shadow-sm"
                style={{ backgroundImage: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primarySoft})` }}
              >
                {s.num}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-stone-900">{s.title}</h3>
                <p className="mt-1 text-sm text-stone-700">{s.body}</p>
                {s.tip ? (
                  <p
                    className="mt-2 inline-block rounded-md px-2 py-1 text-xs"
                    style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
                  >
                    Tip: {s.tip}
                  </p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </Card>

      <Card className="mb-4">
        <div className="border-b border-stone-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-stone-800">Roles & permissions</h2>
          <p className="mt-0.5 text-xs text-stone-500">
            Who can do what. All sign-ins use Cognito (admin portal also requires TOTP).
          </p>
        </div>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          {ROLES.map((r) => (
            <div key={r.name} className="rounded-lg border border-stone-200 bg-stone-50 p-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs font-semibold" style={{ color: BRAND.primary }}>
                  {r.name}
                </span>
                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-stone-500">
                  {r.tag}
                </span>
              </div>
              <ul className="mt-2 space-y-1 text-xs text-stone-700">
                {r.can.map((c) => (
                  <li key={c} className="flex gap-1.5">
                    <span style={{ color: BRAND.success }}>?</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Card>

      <Card className="mb-4">
        <div className="border-b border-stone-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-stone-800">Frequently asked questions</h2>
        </div>
        <div className="divide-y divide-stone-100">
          {FAQS.map((f, i) => (
            <FaqRow key={i} q={f.q} a={f.a} />
          ))}
        </div>
      </Card>

      <Card>
        <div className="border-b border-stone-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-stone-800">Need more help?</h2>
        </div>
        <div className="px-4 py-4 text-sm text-stone-700">
          Email <a className="font-medium hover:underline" style={{ color: BRAND.primary }} href="mailto:support@fixitninjaz.com">
            support@fixitninjaz.com
          </a>{' '}
          and we’ll get back to you the same business day.
        </div>
      </Card>
    </>
  );
}

function FaqRow({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
      className="group"
    >
      <summary className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 text-sm font-medium text-stone-800 hover:bg-stone-50">
        <span>{q}</span>
        <span
          className="text-stone-400 transition-transform"
          style={{ transform: open ? 'rotate(45deg)' : 'rotate(0)' }}
        >
          +
        </span>
      </summary>
      <div className="px-4 pb-4 pt-1 text-sm text-stone-700">{renderAnswer(a)}</div>
    </details>
  );
}

function renderAnswer(text: string): ReactNode {
  // Light auto-link: anything containing fixitninjaz.com or wa.me/ etc. would
  // be an enhancement; for v1 plain text is fine.
  return text;
}
